import {
  AlertTriangle,
  CalendarClock,
  CalendarX,
  CheckCircle,
  Circle,
  Clock,
  EyeOff,
  PauseCircle,
  PowerOff,
  Unplug,
  XCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { retirementBadgeLabel, retirementHint } from "./RetirementNotice";
import { Badge } from "@/components/shadcn/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/shadcn/hover-card";

const ICON_CLASS = "size-3 text-current shrink-0";

export type ModelStatus =
  | "healthy"
  | "unhealthy"
  | "degraded"
  | "suspended"
  | "auto_deactivated"
  | "failed"
  | "deactivated"
  | "disabled"
  | "no_providers"
  | "deprecated"
  | "retiring"
  | "retired"
  | "hidden"
  | "disconnected"
  | "not_checked";

export function deriveModelStatus({
  is_active,
  is_healthy,
  is_deprecated,
  visible,
}: {
  is_active: boolean;
  is_healthy: boolean;
  is_deprecated: boolean;
  visible: boolean;
}): ModelStatus {
  if (is_deprecated) return "deprecated";
  if (!visible) return "hidden";
  if (!is_active) return "no_providers";
  if (!is_healthy) return "unhealthy";
  return "healthy";
}

const STATUS_LABEL: Record<ModelStatus, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  unhealthy: "Unhealthy",
  suspended: "Suspended",
  auto_deactivated: "Deactivated (auto)",
  failed: "Failed",
  deactivated: "Provider disabled",
  disabled: "Disabled",
  no_providers: "No providers",
  deprecated: "Deprecated",
  retiring: "Retiring",
  retired: "Retired",
  hidden: "Hidden",
  disconnected: "Disconnected",
  not_checked: "Not checked",
};

const STATUS_HINT: Record<ModelStatus, string> = {
  healthy: "Healthy",
  degraded: "Recent checks have failed intermittently.",
  unhealthy: "The last connection check failed.",
  suspended:
    "Paused after repeated failures. Reconnect to retry health checks.",
  auto_deactivated:
    "Auto-disabled after persistent failures. Reconnect to restore.",
  failed:
    "Offline: its last check failed. Open the model to see why; switching it on runs the check again.",
  deactivated: "Manually disabled. Re-enable to resume traffic.",
  disabled: "This model is disabled. Toggle it on to resume traffic.",
  no_providers: "No providers added yet.",
  deprecated:
    "This entry is deprecated and will be removed in future releases.",
  retiring:
    "The vendor has announced the date it stops serving this model; move to a replacement before then.",
  retired:
    "The vendor no longer serves this model; requests to it fail. Move to a replacement.",
  hidden: "Hidden from regular consumers.",
  disconnected:
    "The provider is disabled. Connect or re-enable it to use this entry.",
  not_checked: "No health check has run yet. Run one from the provider drawer.",
};

const STATUS_DOT: Record<ModelStatus, string> = {
  healthy: "bg-emerald-500",
  degraded: "bg-amber-500",
  unhealthy: "bg-red-500",
  suspended: "bg-orange-500",
  auto_deactivated: "bg-red-500",
  failed: "bg-red-500",
  deactivated: "bg-slate-400",
  disabled: "bg-slate-400",
  no_providers: "bg-slate-400",
  deprecated: "bg-amber-500",
  retiring: "bg-amber-500",
  retired: "bg-red-500",
  hidden: "bg-slate-400",
  disconnected: "bg-red-500",
  not_checked: "bg-slate-400",
};

interface StatusPopoverProps {
  status: ModelStatus;
  retirementDate?: string | null;
  replacedBy?: string | null;
  consecutiveFailures?: number;
  lastError?: string | null;
  lastCheckedAt?: string | null;
}

function StatusPopoverBody({
  status,
  retirementDate,
  replacedBy,
  consecutiveFailures,
  lastError,
  lastCheckedAt,
}: StatusPopoverProps) {
  const hasFailures = consecutiveFailures != null && consecutiveFailures > 0;
  // No explicit width here — the HoverCardContent wrapper owns sizing
  // so nested callouts can't overflow it.
  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="flex flex-col gap-0.5 min-w-0">
        <p className="text-sm font-medium flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`size-2 rounded-full shrink-0 ${STATUS_DOT[status]}`}
          />
          {statusLabel(status, retirementDate)}
        </p>
        <p className="text-muted-foreground">
          {isRetirementStatus(status) && retirementDate
            ? retirementHint(retirementDate, replacedBy)
            : STATUS_HINT[status]}
        </p>
      </div>

      {(lastCheckedAt || hasFailures) && (
        <div className="rounded-md bg-muted/40 border border-border px-3 py-2 flex flex-col gap-1.5">
          {lastCheckedAt &&
            (() => {
              // Backend serializes naive datetimes without a "Z" suffix; treat
              // them as UTC (matches ProviderConfigDrawer) so the "ago"
              // calculation isn't off by the viewer's timezone offset.
              const isoUtc = lastCheckedAt.endsWith("Z")
                ? lastCheckedAt
                : lastCheckedAt + "Z";
              const date = new Date(isoUtc);
              return (
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground">Last checked</span>
                  <span
                    className="text-foreground tabular-nums truncate"
                    title={date.toLocaleString()}
                  >
                    {formatDistanceToNow(date, { addSuffix: true })}
                  </span>
                </div>
              );
            })()}
          {hasFailures && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-muted-foreground">
                Consecutive failures
              </span>
              <span className="text-foreground tabular-nums">
                {consecutiveFailures}
              </span>
            </div>
          )}
        </div>
      )}

      {lastError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-destructive/80 mb-0.5">
            Last error
          </p>
          <p className="text-destructive break-words">{lastError}</p>
        </div>
      )}
    </div>
  );
}

// Per-status visual config: variant (drives the badge / dot colour) and
// the icon used for both the full and icon-only renderers.
const STATUS_VARIANT: Record<
  ModelStatus,
  {
    variant: React.ComponentProps<typeof Badge>["variant"];
    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }
> = {
  healthy: { variant: "success", Icon: CheckCircle },
  degraded: { variant: "warning", Icon: AlertTriangle },
  unhealthy: { variant: "destructive", Icon: AlertTriangle },
  suspended: { variant: "color-orange", Icon: PauseCircle },
  auto_deactivated: { variant: "destructive", Icon: PowerOff },
  failed: { variant: "destructive", Icon: XCircle },
  deactivated: { variant: "color-slate" },
  disabled: { variant: "color-slate" },
  no_providers: { variant: "color-slate", Icon: Circle },
  deprecated: { variant: "color-amber", Icon: AlertTriangle },
  retiring: { variant: "color-amber", Icon: CalendarClock },
  retired: { variant: "destructive", Icon: CalendarX },
  hidden: { variant: "color-slate", Icon: EyeOff },
  disconnected: { variant: "destructive", Icon: Unplug },
  not_checked: { variant: "color-slate", Icon: Clock },
};

function isRetirementStatus(status: ModelStatus): boolean {
  return status === "retiring" || status === "retired";
}

function statusLabel(
  status: ModelStatus,
  retirementDate?: string | null
): string {
  return isRetirementStatus(status) && retirementDate
    ? retirementBadgeLabel(retirementDate)
    : STATUS_LABEL[status];
}

function renderBadge(
  status: ModelStatus,
  retirementDate?: string | null
): React.ReactNode {
  const { variant, Icon } = STATUS_VARIANT[status];
  return (
    <Badge variant={variant} className="cursor-pointer">
      {Icon && <Icon className={ICON_CLASS} />}
      {statusLabel(status, retirementDate)}
    </Badge>
  );
}

// Per-status icon colour for the icon-only variant — no chip / no
// background, just the Lucide glyph tinted to match the status.
const ICON_ONLY_TINT: Record<ModelStatus, string> = {
  healthy: "text-emerald-600",
  degraded: "text-amber-600",
  unhealthy: "text-red-600",
  suspended: "text-orange-600",
  auto_deactivated: "text-red-600",
  failed: "text-red-600",
  deactivated: "text-slate-500",
  disabled: "text-slate-500",
  no_providers: "text-slate-500",
  deprecated: "text-amber-600",
  retiring: "text-amber-600",
  retired: "text-red-600",
  hidden: "text-slate-500",
  disconnected: "text-red-600",
  not_checked: "text-slate-500",
};

function renderIconOnly(status: ModelStatus): React.ReactNode {
  const { Icon = Circle } = STATUS_VARIANT[status];
  return (
    <Icon
      className={`size-4 shrink-0 cursor-pointer ${ICON_ONLY_TINT[status]}`}
      aria-label={STATUS_LABEL[status]}
    />
  );
}

export function ModelStatusBadge({
  status,
  retirementDate,
  replacedBy,
  lastError,
  consecutiveFailures,
  lastCheckedAt,
  iconOnly = false,
  showHoverCard = true,
}: {
  status: ModelStatus;
  /** Only read for the retirement statuses: it becomes the badge text. */
  retirementDate?: string | null;
  replacedBy?: string | null;
  lastError?: string | null;
  consecutiveFailures?: number;
  lastCheckedAt?: string | null;
  /** Render the status as a circular icon (status hint + meta still
   *  surface via the popover). Useful inside dense table rows where the
   *  full pill would crowd the model name. */
  iconOnly?: boolean;
  /** Set to false to disable the hover card entirely (e.g. when the
   *  surrounding surface already shows the meta alongside the badge,
   *  like the provider drawer's Health section). */
  showHoverCard?: boolean;
}) {
  const badge = iconOnly
    ? renderIconOnly(status)
    : renderBadge(status, retirementDate);

  // No extra detail to show → render the plain badge. We always show the
  // popover for non-healthy states (status-specific hint + last-checked)
  // so users get a tidy explanation even before any errors land.
  const showPopover =
    showHoverCard &&
    (status !== "healthy" ||
      !!lastError ||
      !!lastCheckedAt ||
      (consecutiveFailures != null && consecutiveFailures > 0));

  if (!showPopover) return badge;

  // HoverCard (not Popover) — opens on hover with a short delay, no
  // click needed. Trigger swallows row-level clicks so opening the
  // hover card doesn't double-fire as a row navigation. The
  // `data-suppress-row-tooltip` flag lets parent rows (e.g. ModelsTable)
  // detect hover-over-status and skip their own tooltip so the two
  // hover cards don't stack on top of each other.
  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex" data-suppress-row-tooltip="true">
          {badge}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        collisionPadding={8}
        className="w-80 max-w-[calc(100vw-2rem)] p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <StatusPopoverBody
          status={status}
          retirementDate={retirementDate}
          replacedBy={replacedBy}
          consecutiveFailures={consecutiveFailures}
          lastError={lastError}
          lastCheckedAt={lastCheckedAt}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
